import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/utils/mergeClassNames'
import NEXT_ICON from '@public/assets/button/arrow-chevron-right_white.svg'
import PREVIOUS_ICON from '@public/assets/button/arrow-chevron-left.svg'

import {
  LOCAL_LAST_WIZARD_STEP_KEYS,
  WELCOME_WIZARD_STEPS,
  WELCOME_WIZARD_STEPS_KEY,
  WELCOME_WIZARD_STEP_SEQUENCE,
} from '../constants'
import type { ConnectedAppStatus, WizardStepKey } from '../types'
import WelcomeWizardStepButton from './WelcomeWizardStepButton'
import WelcomeWizardStepContent from './WelcomeWizardStepContent'

type WelcomeWizardProps = {
  chromeExtensionStatus: ConnectedAppStatus
  desktopAgentStatus: ConnectedAppStatus
  edgeExtensionStatus: ConnectedAppStatus
  onClose: () => void
}

const getInitialWizardStepKey = (): WizardStepKey => {
  const savedStep = localStorage.getItem(LOCAL_LAST_WIZARD_STEP_KEYS)
  const savedStepByKey = WELCOME_WIZARD_STEP_SEQUENCE.find(
    step => step.key === savedStep
  )

  if (savedStepByKey) return savedStepByKey.key

  return WELCOME_WIZARD_STEP_SEQUENCE[0].key
}

const WelcomeWizard = ({
  chromeExtensionStatus,
  desktopAgentStatus,
  edgeExtensionStatus,
  onClose,
}: WelcomeWizardProps) => {
  const [activeStepKey, setActiveStepKey] = useState<WizardStepKey>(
    getInitialWizardStepKey
  )

  const activeStepIndex = WELCOME_WIZARD_STEP_SEQUENCE.findIndex(
    step => step.key === activeStepKey
  )
  const isLastStep = activeStepKey === WELCOME_WIZARD_STEPS_KEY.WelcomeComplete
  const activeParentKey =
    WELCOME_WIZARD_STEPS.find(
      step =>
        step.key === activeStepKey ||
        step.children?.some(child => child.key === activeStepKey)
    )?.key ?? activeStepKey

  const handlePrevious = () => {
    setActiveStepKey(
      WELCOME_WIZARD_STEP_SEQUENCE[Math.max(0, activeStepIndex - 1)].key
    )
  }

  const handleNext = () => {
    if (isLastStep) {
      localStorage.removeItem(LOCAL_LAST_WIZARD_STEP_KEYS)
      return onClose()
    }
    setActiveStepKey(WELCOME_WIZARD_STEP_SEQUENCE[activeStepIndex + 1].key)
  }

  const handleWelcomeWizardClose = () => {
    localStorage.setItem(LOCAL_LAST_WIZARD_STEP_KEYS, activeStepKey)
    onClose()
  }

  return createPortal(
    <div
      aria-label="Welcome to MIRA"
      aria-modal="true"
      className="fixed inset-0 z-[1000000] flex items-center justify-center bg-black/50 p-[30px]"
      role="dialog"
    >
      <div className="flex h-full max-h-[840px] w-full max-w-[1380px] flex-col overflow-hidden rounded-3xl bg-white shadow-[0_4px_8px_3px_rgba(0,0,0,0.15),0_1px_3px_rgba(0,0,0,0.3)]">
        <header className="flex h-20 shrink-0 items-center justify-between border-b border-[#d9d9d9] px-[30px]">
          <h1 className="text-lg font-semibold text-[#141414]">
            🎓 Welcome to MIRA 👋
          </h1>
          <button
            aria-label="Close welcome wizard"
            className="flex h-12 w-12 items-center justify-center rounded text-2xl text-[#333] hover:bg-neutral-100"
            onClick={handleWelcomeWizardClose}
            type="button"
          >
            ×
          </button>
        </header>
        <div className="flex min-h-0 flex-1">
          <nav
            aria-label="Welcome steps"
            className="w-[280px] shrink-0 border-r border-[#d9d9d9] py-[30px]"
          >
            {WELCOME_WIZARD_STEPS.map(step => (
              <React.Fragment key={step.key}>
                {!step.hidden && (
                  <WelcomeWizardStepButton
                    isActive={activeStepKey === step.key}
                    isCompleted={
                      WELCOME_WIZARD_STEP_SEQUENCE.findIndex(
                        sequenceStep => sequenceStep.key === step.key
                      ) < activeStepIndex
                    }
                    onSelect={setActiveStepKey}
                    step={step}
                  />
                )}
                {step.key === activeParentKey &&
                  step.children?.map(child => (
                    <WelcomeWizardStepButton
                      isActive={activeStepKey === child.key}
                      isChild
                      isCompleted={
                        WELCOME_WIZARD_STEP_SEQUENCE.findIndex(
                          sequenceStep => sequenceStep.key === child.key
                        ) < activeStepIndex
                      }
                      key={child.key}
                      onSelect={setActiveStepKey}
                      step={child}
                    />
                  ))}
              </React.Fragment>
            ))}
          </nav>
          <main className="relative flex min-w-[500px] flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto p-[30px] pb-24">
              <WelcomeWizardStepContent
                activeStepKey={activeStepKey}
                chromeExtensionStatus={chromeExtensionStatus}
                desktopAgentStatus={desktopAgentStatus}
                edgeExtensionStatus={edgeExtensionStatus}
                onSkip={handleNext}
              />
            </div>
            <div className="absolute bottom-[30px] right-[30px] flex gap-2">
              <button
                aria-label="Previous step"
                className={cn(
                  'flex size-11 items-center justify-center rounded-[40px] border border-[#dadada]',
                  activeStepIndex === 0
                    ? 'invisible'
                    : 'bg-white hover:bg-[#e2e1e3]'
                )}
                disabled={activeStepIndex === 0}
                onClick={handlePrevious}
                type="button"
              >
                <img alt="" src={PREVIOUS_ICON} />
              </button>
              <button
                aria-label={isLastStep ? 'Finish welcome wizard' : 'Next step'}
                className={cn(
                  'flex h-11 items-center justify-center rounded-[40px] border-0 bg-[#333] text-white hover:bg-black',
                  isLastStep ? 'px-3' : 'w-11 px-3'
                )}
                onClick={handleNext}
                type="button"
              >
                {isLastStep ? 'Close' : <img alt="" src={NEXT_ICON} />}
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default WelcomeWizard
